import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Portal,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
    CircularProgress,
    Checkbox,
    Tooltip,
    LinearProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ClearIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SearchIcon from '@mui/icons-material/Search';
import AddCardIcon from '@mui/icons-material/AddCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PaymentsIcon from '@mui/icons-material/Payments';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import clientSavedCardsService from '../../../Services/clientSavedCards.service';
import masavService from '../../../Services/masav.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import s from './CreditCardBulkChargeModal.module.scss';

const formatMoney = (v, lang) =>
    Number(v || 0).toLocaleString(['he', 'ar'].includes(lang) ? 'he-IL' : 'en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const fieldSx = {
    height: '40px',
    padding: '0 12px',
    borderRadius: '10px',
    background: '#f5f6fd',
    width: '100%',
    border: '1px solid #e5e7f6',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',
    '&:hover': { borderColor: '#c9cff5' },
    '&.Mui-focused, &:focus-within': {
        borderColor: '#304FFF',
        boxShadow: '0 0 0 3px rgba(48, 79, 255, 0.12)',
        background: '#fff',
    },
    '&.Mui-disabled': { opacity: 0.7 },
};

/** Normalize a MASAV draft row regardless of casing. */
const normalizeMasavRow = (raw) => ({
    caseId: raw.caseId ?? raw.CaseId,
    clientName: raw.clientName ?? raw.ClientName ?? '',
    netBalance: Number(raw.netBalance ?? raw.NetBalance ?? 0),
    payableAmount: Number(raw.payableAmount ?? raw.PayableAmount ?? 0),
    isPaymentArrangementApplied: !!(raw.isPaymentArrangementApplied ?? raw.IsPaymentArrangementApplied),
    paymentArrangementAmount: Number(raw.paymentArrangementAmount ?? raw.PaymentArrangementAmount ?? 0) || null,
});

/**
 * Credit-card bulk charge modal — mirrors the MASAV draft modal flow but for
 * credit-card charges using each client's saved card token (Tranzila or Grow —
 * the server routes each charge by the token's provider):
 *  - Pulls the same balances as MASAV (`masavService.getMasavBulkDraft`).
 *  - Cross-joins with saved cards (`clientSavedCardsService.list`).
 *  - Lets the user pick clients and a *custom amount* per client (defaulted
 *    to the MASAV `payableAmount`).
 *  - Loops `clientSavedCardsService.charge` per included row. The server's
 *    `LedgerBridge` records each successful charge as a CreditCard payment on
 *    the client's ledger, so partial success is fine — only failed rows are
 *    reported back.
 */
const CreditCardBulkChargeModal = ({
    open,
    onClose,
    direction = 'rtl',
    leadingBusiness = false,
    client,
    /** Same set the CollectionPage uses for MASAV (active client filter). */
    filterCaseIds,
    onOpenSavedCardsFor,
    onChargeFinished,
    /** Current clearing provider (0=Tranzila, 1=Grow) — tokens are not portable across providers. */
    terminalProviderCode = null,
}) => {
    const { t, i18n } = useTranslation();
    const isRtl = direction === 'rtl';
    const lang = i18n.language;
    const accountantId = client?.accountantId;

    // Date range — same defaults as MasavDraftModal (startDate=null open-start, endDate=today).
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(() => new Date());

    const [loadingBalances, setLoadingBalances] = useState(false);
    const [loadingCards, setLoadingCards] = useState(false);
    const [loadError, setLoadError] = useState('');

    /** caseId → normalized MASAV row */
    const [balancesByCase, setBalancesByCase] = useState({});
    /** caseId → saved-card row */
    const [cardsByCase, setCardsByCase] = useState({});

    const [search, setSearch] = useState('');
    const [perClient, setPerClient] = useState({});

    // Date is no longer captured in the UI — the server stamps the payment with
    // the moment the provider charge actually succeeded, which is what the
    // ledger entry should reflect.
    const [description, setDescription] = useState('');
    const [reference, setReference] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState({});
    /** caseId → server error message, shown in the per-row tooltip. */
    const [rowErrors, setRowErrors] = useState({});
    const lastRunSummaryRef = useRef(null);

    /** Reload balances (MASAV draft) for the current date range / filter. */
    const loadBalances = useCallback(async () => {
        if (!open) return;
        if (!leadingBusiness && !client?.caseId) return;
        if (leadingBusiness && !accountantId) return;
        setLoadingBalances(true);
        setLoadError('');
        try {
            // Single-client mode: scope the draft request to that one case.
            const caseIds = leadingBusiness
                ? (Array.isArray(filterCaseIds) && filterCaseIds.length > 0 ? filterCaseIds : undefined)
                : [client.caseId];
            const data = await masavService.getMasavBulkDraft(
                accountantId,
                startDate,
                endDate,
                caseIds
            );
            const rows = Array.isArray(data?.rows ?? data?.Rows)
                ? (data.rows ?? data.Rows).map(normalizeMasavRow)
                : [];
            const map = {};
            rows.forEach((r) => { if (r.caseId) map[String(r.caseId)] = r; });
            setBalancesByCase(map);
        } catch (e) {
            console.error(e);
            setLoadError(e?.message || t('Error'));
            setBalancesByCase({});
        } finally {
            setLoadingBalances(false);
        }
    }, [open, leadingBusiness, accountantId, client?.caseId, filterCaseIds, startDate, endDate, t]);

    /** Reload saved cards. */
    const loadCards = useCallback(async () => {
        if (!open) return;
        if (!leadingBusiness && !client?.caseId) return;
        setLoadingCards(true);
        try {
            const data = await clientSavedCardsService.list({
                leadingBusiness,
                clientCaseId: leadingBusiness ? undefined : client?.caseId,
            });
            const list = Array.isArray(data) ? data : [];
            const map = {};
            list.forEach((r) => {
                if (!r.caseId || !r.savedCardId) return;
                // Tokens saved under a different clearing provider cannot be charged on the
                // current terminal (the server rejects them with 400) — treat as "no card"
                // so the row offers re-saving a card instead of a guaranteed failure.
                if (terminalProviderCode != null && r.providerCode != null
                    && Number(r.providerCode) !== Number(terminalProviderCode)) return;
                map[String(r.caseId)] = r;
            });
            setCardsByCase(map);
        } catch (e) {
            console.error(e);
            setCardsByCase({});
        } finally {
            setLoadingCards(false);
        }
    }, [open, leadingBusiness, client?.caseId, terminalProviderCode]);

    useEffect(() => { loadBalances(); }, [loadBalances]);
    useEffect(() => { loadCards(); }, [loadCards]);

    useEffect(() => {
        if (!open) return;
        setDescription('');
        setReference('');
        setSearch('');
        setProgress({});
        setRowErrors({});
        setPerClient({});
        lastRunSummaryRef.current = null;
    }, [open]);

    /** Merged rows: every saved-card client + their balance (if any). */
    const mergedRows = useMemo(() => {
        const rows = [];
        Object.keys(cardsByCase).forEach((caseId) => {
            const card = cardsByCase[caseId];
            const bal = balancesByCase[caseId] || {};
            rows.push({
                caseId,
                clientName: card.clientName || bal.clientName || caseId,
                netBalance: Number(bal.netBalance ?? 0),
                payableAmount: Number(bal.payableAmount ?? 0),
                isPaymentArrangementApplied: !!bal.isPaymentArrangementApplied,
                paymentArrangementAmount: bal.paymentArrangementAmount ?? null,
                last4: card.last4,
                savedCardId: card.savedCardId,
            });
        });
        // For non-leading-business mode also surface the single client when no card yet.
        if (!leadingBusiness && client?.caseId && !cardsByCase[client.caseId]) {
            const bal = balancesByCase[String(client.caseId)] || {};
            rows.push({
                caseId: String(client.caseId),
                clientName: client.name || bal.clientName || client.caseId,
                netBalance: Number(bal.netBalance ?? 0),
                payableAmount: Number(bal.payableAmount ?? 0),
                isPaymentArrangementApplied: !!bal.isPaymentArrangementApplied,
                paymentArrangementAmount: bal.paymentArrangementAmount ?? null,
                last4: null,
                savedCardId: null,
            });
        }
        // Sort: clients with positive payable first, then by name.
        rows.sort((a, b) => {
            if ((b.payableAmount > 0) !== (a.payableAmount > 0)) {
                return (b.payableAmount > 0 ? 1 : 0) - (a.payableAmount > 0 ? 1 : 0);
            }
            return String(a.clientName).localeCompare(String(b.clientName));
        });
        return rows;
    }, [cardsByCase, balancesByCase, leadingBusiness, client]);

    /** Pre-fill amount inputs once data lands. */
    useEffect(() => {
        if (!open) return;
        setPerClient((prev) => {
            const next = { ...prev };
            mergedRows.forEach((row) => {
                if (!next[row.caseId]) {
                    const suggested = row.payableAmount > 0
                        ? row.payableAmount.toFixed(2)
                        : '';
                    next[row.caseId] = {
                        include: row.payableAmount > 0 && !!row.savedCardId,
                        amount: suggested,
                    };
                }
            });
            return next;
        });
    }, [mergedRows, open]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return mergedRows;
        return mergedRows.filter((r) =>
            String(r.clientName || '').toLowerCase().includes(q) ||
            String(r.caseId || '').toLowerCase().includes(q) ||
            String(r.last4 || '').toLowerCase().includes(q)
        );
    }, [mergedRows, search]);

    const eligibleRows = useMemo(
        () => filteredRows.filter((r) => !!r.savedCardId),
        [filteredRows]
    );
    const ineligibleRows = useMemo(
        () => filteredRows.filter((r) => !r.savedCardId),
        [filteredRows]
    );

    const selectedRows = useMemo(
        () => eligibleRows.filter((r) => {
            const s = perClient[r.caseId];
            return s?.include && Number(parseFloat(s.amount)) > 0;
        }),
        [eligibleRows, perClient]
    );

    const grandTotal = useMemo(
        () => selectedRows.reduce((sum, r) => sum + Number(parseFloat(perClient[r.caseId]?.amount) || 0), 0),
        [selectedRows, perClient]
    );

    const allSelected = eligibleRows.length > 0 && eligibleRows.every((r) => perClient[r.caseId]?.include);

    const validationError = useMemo(() => {
        if (!description.trim()) return t('CreditCardBulkCharge.errDescription');
        if (!selectedRows.length) return t('CreditCardBulkCharge.errSelect');
        return '';
    }, [description, selectedRows, t]);

    const toggleAll = () => {
        const next = { ...perClient };
        const turnOn = !allSelected;
        eligibleRows.forEach((r) => {
            next[r.caseId] = {
                include: turnOn,
                amount: next[r.caseId]?.amount ?? (r.payableAmount > 0 ? r.payableAmount.toFixed(2) : ''),
            };
        });
        setPerClient(next);
    };

    const updateRow = (caseId, patch) => {
        setPerClient((prev) => ({
            ...prev,
            [caseId]: { include: false, amount: '', ...prev[caseId], ...patch },
        }));
    };

    const handleRefresh = () => {
        loadBalances();
        loadCards();
    };

    const handleSubmit = async () => {
        if (validationError || submitting) return;

        const confirm = await Swal.fire({
            title: t('CreditCardBulkCharge.confirmTitle'),
            html: t('CreditCardBulkCharge.confirmBody', {
                count: selectedRows.length,
                total: formatMoney(grandTotal, lang),
            }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('CreditCardBulkCharge.confirmYes'),
            cancelButtonText: t('Cancel'),
            confirmButtonColor: '#304FFF',
        });
        if (!confirm.isConfirmed) return;

        setSubmitting(true);
        setProgress({});
        setRowErrors({});
        let ok = 0;
        const failures = [];
        const successes = [];

        try {
            // Process sequentially so partial state is consistent if user cancels.
            for (const row of selectedRows) {
                setProgress((p) => ({ ...p, [row.caseId]: 'pending' }));
                try {
                    const amount = Number(parseFloat(perClient[row.caseId]?.amount) || 0);
                    const resp = await clientSavedCardsService.charge({
                        clientCaseId: row.caseId,
                        savedCardId: row.savedCardId,
                        amount,
                        description: description.trim(),
                        reference: reference.trim() || null,
                    });
                    if (resp && (resp.status === 1 || resp.Status === 1)) {
                        ok += 1;
                        successes.push({ caseId: row.caseId, clientName: row.clientName, amount });
                        setProgress((p) => ({ ...p, [row.caseId]: 'success' }));
                        // Clear the amount field on success so a partial retry only re-charges the remaining ones.
                        setPerClient((prev) => ({
                            ...prev,
                            [row.caseId]: { include: false, amount: '' },
                        }));
                    } else {
                        const msg = resp?.errorMessage || resp?.ErrorMessage || t('CreditCardBulkCharge.failedRow');
                        failures.push({ caseId: row.caseId, clientName: row.clientName, message: msg });
                        setRowErrors((prev) => ({ ...prev, [row.caseId]: msg }));
                        setProgress((p) => ({ ...p, [row.caseId]: 'failed' }));
                    }
                } catch (e) {
                    const msg = e?.message || String(e);
                    failures.push({ caseId: row.caseId, clientName: row.clientName, message: msg });
                    setRowErrors((prev) => ({ ...prev, [row.caseId]: msg }));
                    setProgress((p) => ({ ...p, [row.caseId]: 'failed' }));
                }
            }

            lastRunSummaryRef.current = { ok, failures, successes };

            // Refresh balances so the user sees the lower payable amount after partial success.
            await loadBalances();

            if (ok && !failures.length) toast.success(t('CreditCardBulkCharge.allSuccess', { count: ok }));
            else if (ok && failures.length) {
                toast.warning(
                    `${t('CreditCardBulkCharge.partial', { ok, failed: failures.length })} — ${failures[0].message}`,
                    { autoClose: 10000 },
                );
            } else {
                const detail = failures.map((f) => `${f.clientName}: ${f.message}`).join('\n');
                toast.error(
                    detail || t('CreditCardBulkCharge.allFailed'),
                    { autoClose: 12000 },
                );
            }

            onChargeFinished?.({ ok, failures, successes });

            if (!failures.length) {
                onClose?.();
            }
        } finally {
            setSubmitting(false);
        }
    };

    /** Closing is blocked while a charge run is in progress. */
    const handleClose = () => {
        if (submitting) return;
        onClose?.();
    };

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented || submitting) return;
            // Let stacked popups (SavedCards dialog, SweetAlert, month picker list) handle their own Escape.
            if (document.querySelector('.MuiDialog-root, .swal2-container, [role="listbox"]')) return;
            onClose?.();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, submitting, onClose]);

    const runStats = useMemo(() => {
        const acc = { charging: 0, success: 0, failed: 0 };
        Object.values(progress).forEach((v) => {
            if (v === 'pending') acc.charging += 1;
            else if (v === 'success') acc.success += 1;
            else if (v === 'failed') acc.failed += 1;
        });
        return acc;
    }, [progress]);

    if (!open) return null;

    const showDateRange = leadingBusiness;
    const loading = loadingBalances || loadingCards;
    const withCardCount = mergedRows.filter((r) => !!r.savedCardId).length;
    const someSelected = eligibleRows.some((r) => perClient[r.caseId]?.include);
    const dateRangeInvalid = !!(startDate && endDate && startDate.getTime() > endDate.getTime());
    const queuedCount = submitting ? selectedRows.filter((r) => !progress[r.caseId]).length : 0;
    const runDone = runStats.success + runStats.failed;
    const runTotal = runDone + runStats.charging + queuedCount;
    const showRunPanel = submitting || runDone > 0;
    const searchHidesAll = !!search.trim() && mergedRows.some((r) => !!r.savedCardId);

    const handleStartDateChange = (date) => {
        if (date) setStartDate(date);
    };
    const handleEndDateChange = (date) => {
        if (date) setEndDate(date);
    };

    const renderStatus = (row, state) => {
        const rowProgress = progress[row.caseId];
        if (rowProgress === 'pending') {
            return (
                <span className={s.chip}>
                    <CircularProgress size={10} color="inherit" />
                    {t('Processing')}
                </span>
            );
        }
        if (rowProgress === 'success') {
            return (
                <span className={`${s.chip} ${s.chipSuccess}`}>
                    <CheckCircleIcon className={s.chipIcon} />
                    {t('Succeeded')}
                </span>
            );
        }
        if (rowProgress === 'failed') {
            const msg = rowErrors[row.caseId] || t('CreditCardBulkCharge.failedRow');
            return (
                <Tooltip title={msg}>
                    <div className={s.statusCell}>
                        <span className={`${s.chip} ${s.chipError}`}>
                            <ErrorOutlineIcon className={s.chipIcon} />
                            {t('Failed')}
                        </span>
                        <span className={s.rowError}>{msg}</span>
                    </div>
                </Tooltip>
            );
        }
        if (submitting && state.include && Number(parseFloat(state.amount)) > 0) {
            return <span className={`${s.chip} ${s.chipMuted}`}>{t('Pending')}</span>;
        }
        return <span className={s.muted}>—</span>;
    };

    const renderTable = () => (
        <div className={s.tableWrap} dir={direction}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }}>
                            <Tooltip
                                title={allSelected
                                    ? t('CreditCardBulkCharge.deselectAll')
                                    : t('CreditCardBulkCharge.selectAll')}
                            >
                                <span>
                                    <Checkbox
                                        size="small"
                                        checked={allSelected}
                                        indeterminate={someSelected && !allSelected}
                                        onChange={toggleAll}
                                        disabled={submitting || eligibleRows.length === 0}
                                    />
                                </span>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{t('ClientName')}</TableCell>
                        <TableCell>{t('Case')}</TableCell>
                        <TableCell className={s.num}>{t('MasavNetBalance')}</TableCell>
                        <TableCell className={s.num}>{t('MasavPayableAmount')}</TableCell>
                        <TableCell className={s.center}>{t('CreditCard')}</TableCell>
                        <TableCell className={s.num}>{t('CreditCardBulkCharge.amountToCharge')}</TableCell>
                        <TableCell>{t('Status')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredRows.map((row) => {
                        const state = perClient[row.caseId] || { include: false, amount: '' };
                        const hasCard = !!row.savedCardId;
                        const rowClass = progress[row.caseId] === 'failed'
                            ? s.rowFailed
                            : !hasCard ? s.rowNoCard : state.include ? s.rowSelected : undefined;
                        return (
                            <TableRow key={row.caseId} className={rowClass}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={!!state.include}
                                        disabled={submitting || !hasCard}
                                        onChange={(e) => updateRow(row.caseId, { include: e.target.checked })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <span className={s.clientName}>{row.clientName}</span>
                                </TableCell>
                                <TableCell>
                                    <span className={s.clientCase}>{row.caseId}</span>
                                </TableCell>
                                <TableCell className={s.num}>
                                    <span className={row.netBalance < 0 ? s.negative : undefined}>
                                        {beautifyNumber(row.netBalance)}
                                    </span>
                                </TableCell>
                                <TableCell className={s.num}>
                                    <div className={s.payableCell}>
                                        <span>{row.payableAmount > 0 ? row.payableAmount.toFixed(2) : '—'}</span>
                                        {row.isPaymentArrangementApplied && (
                                            <span className={s.chip}>
                                                {t('PaymentArrangementBadge', {
                                                    amount: Number(row.paymentArrangementAmount || row.payableAmount).toFixed(2),
                                                })}
                                            </span>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className={s.center}>
                                    {hasCard ? (
                                        <Tooltip title={t('SavedCards.manageForClient', {
                                            defaultValue: 'Click to manage / replace / update CVV for {{name}}',
                                            name: row.clientName,
                                        })}>
                                            <button
                                                type="button"
                                                className={s.cardChip}
                                                onClick={() => onOpenSavedCardsFor?.(row.caseId, row.clientName)}
                                            >
                                                <CreditCardIcon className={s.chipIcon} />
                                                {`•••• ${row.last4 || '----'}`}
                                            </button>
                                        </Tooltip>
                                    ) : (
                                        <button
                                            type="button"
                                            className={`${s.ghostBtn} ${s.smallBtn}`}
                                            onClick={() => onOpenSavedCardsFor?.(row.caseId, row.clientName)}
                                            disabled={submitting}
                                        >
                                            <AddCardIcon fontSize="small" />
                                            {t('SavedCards.add')}
                                        </button>
                                    )}
                                </TableCell>
                                <TableCell className={s.num}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={state.amount}
                                        onChange={(e) => updateRow(row.caseId, { amount: e.target.value })}
                                        disabled={submitting || !hasCard}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                                        }}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        className={s.amountField}
                                    />
                                </TableCell>
                                <TableCell>{renderStatus(row, state)}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );

    const renderEmpty = () => (
        <div className={s.emptyState}>
            <CreditCardIcon className={s.emptyIcon} />
            {searchHidesAll ? (
                <>
                    <p className={s.muted}>{t('NoResultsFound')}</p>
                    <button type="button" className={`${s.secondaryBtn} ${s.smallBtn}`} onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                        {t('Clear')}
                    </button>
                </>
            ) : (
                <>
                    <p className={s.muted}>{t('CreditCardBulkCharge.noClients')}</p>
                    {!leadingBusiness && (
                        <button
                            type="button"
                            className={s.secondaryBtn}
                            onClick={() => onOpenSavedCardsFor?.(client?.caseId, client?.name)}
                        >
                            <AddCardIcon fontSize="small" />
                            {t('SavedCards.add')}
                        </button>
                    )}
                </>
            )}
        </div>
    );

    return (
        <Portal>
            <div className={`${s.overlay} ${isRtl ? s.rtl : s.ltr}`} onClick={handleClose}>
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={s.modal}>
                        <div className={s.body}>
                            <div className={s.header}>
                                <Tooltip title={t('Close')}>
                                    <span>
                                        <IconButton
                                            onClick={handleClose}
                                            disabled={submitting}
                                            className={s.closeIcon}
                                            aria-label={t('Close')}
                                        >
                                            <CancelIcon className={s.closeIcon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div className={s.headerTitle}>
                                    <CreditCardIcon />
                                    {t('CreditCardBulkCharge.title')}
                                </div>
                                <div />
                            </div>
                            <p className={s.subtitle}>{t('CreditCardBulkCharge.subtitle')}</p>
                            {loading && mergedRows.length > 0 && <LinearProgress className={s.topProgress} />}

                            <div className={s.scroll}>
                                {/* Shared inputs — description (required) is written to the
                                    ledger Payment.Description on success; reference is written
                                    to Payment.Reference (with a stable provider-transaction-id
                                    suffix for idempotency). The payment date is stamped
                                    server-side at the moment the provider charge succeeds. */}
                                <div className={s.toolbar}>
                                    <div className={`${s.toolbarGroup} ${s.toolbarGroupGrow}`}>
                                        <div className={`${s.field} ${s.fieldDescription}`}>
                                            <span className={s.fieldLabel}>
                                                {t('Description')}
                                                <span className={s.required}>*</span>
                                            </span>
                                            <TextField
                                                fullWidth
                                                variant="standard"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                disabled={submitting}
                                                InputProps={{ disableUnderline: true, sx: fieldSx }}
                                                inputProps={{ dir: direction, 'aria-label': t('Description'), 'aria-required': true }}
                                            />
                                        </div>
                                        <div className={`${s.field} ${s.fieldReference}`}>
                                            <span className={s.fieldLabel}>
                                                {t('Reference')} ({t('Optional')})
                                            </span>
                                            <TextField
                                                fullWidth
                                                variant="standard"
                                                value={reference}
                                                onChange={(e) => setReference(e.target.value)}
                                                disabled={submitting}
                                                InputProps={{ disableUnderline: true, sx: fieldSx }}
                                                inputProps={{ dir: direction, 'aria-label': t('Reference') }}
                                            />
                                        </div>
                                    </div>

                                    <div className={s.toolbarGroup}>
                                        {showDateRange && (
                                            <>
                                                <span className={s.muted} style={{ alignSelf: 'center' }}>
                                                    {t('CreditCardBulkCharge.balancePeriod')}
                                                </span>
                                                <div className={s.dateField}>
                                                    <span className={s.fieldLabel}>
                                                        {t('StartDate')} ({t('Optional')})
                                                        {startDate && (
                                                            <Tooltip title={t('Clear')}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => setStartDate(null)}
                                                                    className={s.clearDate}
                                                                    aria-label={t('Clear')}
                                                                >
                                                                    <ClearIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </span>
                                                    <CustomMonthDatePicker
                                                        date={startDate}
                                                        onDateChange={handleStartDateChange}
                                                        direction={direction}
                                                        height="40px"
                                                    />
                                                </div>
                                                <div className={s.dateField}>
                                                    <span className={s.fieldLabel}>{t('EndDate')}</span>
                                                    <CustomMonthDatePicker
                                                        date={endDate}
                                                        onDateChange={handleEndDateChange}
                                                        direction={direction}
                                                        height="40px"
                                                    />
                                                </div>
                                                {dateRangeInvalid && (
                                                    <span className={s.dateError}>{t('MasavDateRangeInvalid')}</span>
                                                )}
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className={`${s.secondaryBtn} ${s.controlBtn}`}
                                            onClick={handleRefresh}
                                            disabled={loading || submitting}
                                        >
                                            {loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
                                            {t('Refresh')}
                                        </button>
                                    </div>
                                </div>

                                {loadError && (
                                    <Alert
                                        dir={direction}
                                        severity="error"
                                        className={s.alert}
                                        action={(
                                            <button
                                                type="button"
                                                className={`${s.dangerBtn} ${s.smallBtn}`}
                                                onClick={handleRefresh}
                                                disabled={loading || submitting}
                                            >
                                                {t('TryAgain')}
                                            </button>
                                        )}
                                    >
                                        {loadError}
                                    </Alert>
                                )}

                                {mergedRows.length > 0 && (
                                    <div className={s.stats}>
                                        <div className={s.statCard}>
                                            <span className={s.statLabel}>{t('Clients')}</span>
                                            <span className={s.statValue}>{mergedRows.length}</span>
                                        </div>
                                        <div className={s.statCard}>
                                            <span className={s.statLabel}>
                                                {t('CreditCardBulkCharge.statWithCard', { defaultValue: 'With saved card' })}
                                            </span>
                                            <span className={s.statValue}>{withCardCount}</span>
                                        </div>
                                        <div className={s.statCard}>
                                            <span className={s.statLabel}>{t('MasavStatSelected')}</span>
                                            <span className={s.statValue}>{selectedRows.length}</span>
                                        </div>
                                        <div className={`${s.statCard} ${s.statCardAccent}`}>
                                            <span className={s.statLabel}>{t('CreditCardBulkCharge.amountToCharge')}</span>
                                            <span className={s.statValue}>₪{formatMoney(grandTotal, lang)}</span>
                                        </div>
                                    </div>
                                )}

                                {showRunPanel && (
                                    <div className={s.runPanel}>
                                        <div className={s.runPanelTop}>
                                            {submitting && (
                                                <span className={s.chip}>
                                                    <CircularProgress size={10} color="inherit" />
                                                    {t('Processing')}
                                                </span>
                                            )}
                                            {runStats.success > 0 && (
                                                <span className={`${s.chip} ${s.chipSuccess}`}>
                                                    <CheckCircleIcon className={s.chipIcon} />
                                                    {t('Succeeded')}: {runStats.success}
                                                </span>
                                            )}
                                            {runStats.failed > 0 && (
                                                <span className={`${s.chip} ${s.chipError}`}>
                                                    <ErrorOutlineIcon className={s.chipIcon} />
                                                    {t('Failed')}: {runStats.failed}
                                                </span>
                                            )}
                                            {submitting && runTotal > 0 && (
                                                <span className={s.runCount}>{runDone} / {runTotal}</span>
                                            )}
                                        </div>
                                        {submitting && (
                                            <LinearProgress
                                                className={s.runBar}
                                                variant={runTotal > 0 ? 'determinate' : 'indeterminate'}
                                                value={runTotal > 0 ? (runDone / runTotal) * 100 : 0}
                                            />
                                        )}
                                    </div>
                                )}

                                {leadingBusiness && mergedRows.length > 0 && (
                                    <div className={s.searchRow}>
                                        <TextField
                                            size="small"
                                            placeholder={t('CreditCardBulkCharge.searchPlaceholder')}
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            className={s.searchField}
                                            inputProps={{ dir: direction, 'aria-label': t('CreditCardBulkCharge.searchPlaceholder') }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" color="action" aria-hidden />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: search ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setSearch('')} aria-label={t('Clear')}>
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />
                                    </div>
                                )}

                                {loading && !mergedRows.length ? (
                                    <div className={s.loading}>
                                        <CircularProgress className={s.spinner} />
                                    </div>
                                ) : eligibleRows.length === 0 ? (
                                    renderEmpty()
                                ) : (
                                    renderTable()
                                )}

                                {leadingBusiness && ineligibleRows.length > 0 && eligibleRows.length > 0 && (
                                    <div className={s.notice}>
                                        <AddCardIcon fontSize="small" />
                                        <span>{t('CreditCardBulkCharge.withoutCard', { count: ineligibleRows.length })}</span>
                                    </div>
                                )}
                            </div>

                            <div className={s.footer}>
                                {leadingBusiness && (
                                    <button
                                        type="button"
                                        className={s.ghostBtn}
                                        onClick={() => onOpenSavedCardsFor?.(null, null)}
                                        disabled={submitting}
                                    >
                                        <CreditCardIcon fontSize="small" />
                                        {t('SavedCards.manage')}
                                    </button>
                                )}
                                <div className={s.footerSummary}>
                                    {selectedRows.length > 0 && (
                                        <span className={s.footerTotals}>
                                            {t('MasavFooterSelected', {
                                                count: selectedRows.length,
                                                sum: formatMoney(grandTotal, lang),
                                            })}
                                        </span>
                                    )}
                                    {validationError && <span className={s.footerHint}>{validationError}</span>}
                                </div>
                                <span className={s.footerSpacer} />
                                <button type="button" className={s.secondaryBtn} onClick={handleClose} disabled={submitting}>
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="button"
                                    className={s.primaryBtn}
                                    onClick={handleSubmit}
                                    disabled={!!validationError || submitting}
                                >
                                    {submitting ? <CircularProgress color="inherit" size={16} /> : <PaymentsIcon fontSize="small" />}
                                    {t('CreditCardBulkCharge.chargeButton', { total: formatMoney(grandTotal, lang) })}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default CreditCardBulkChargeModal;
